import * as React from 'react';
import * as styles from './styles.scss';
import { formatShortRelativeDate } from 'owa-observable-datetime';
import { getAttachmentPreviews } from '../selectors/getAttachmentPreviews';
import { observer } from 'mobx-react-lite';
import { PersonaIcon, AttachmentDocContainer } from 'owa-timestream-asset-sdk';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import type { SearchResultItem } from 'owa-timestream-search-orchestration';
import classNames from 'classnames';
import MailSearchHeader from './MailSearchHeader';
import type Message from 'owa-service/lib/contract/Message';
import popoutReadingPane from 'owa-popout-utils/lib/utils/popoutReadingPane';
import { SearchResultOptions } from 'owa-timestream-search-view';
import { useInnerFocusTracker } from 'owa-react-hooks/lib/useInnerFocusTracker';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { Icon } from '@fluentui/react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';

export interface MailSearchResultProps {
    item: SearchResultItem;
}

export default observer(function MailSearchResult(props: MailSearchResultProps) {
    const { item } = props;
    const messageItem = item.data as Message;

    const ref = React.useRef<HTMLDivElement>(null);
    const { hasFocusState, onFocusCapture, onBlurCapture } = useInnerFocusTracker(ref);
    const [optionsOpen, setOptionsOpen, resetOptionsOpen] = useBooleanState(false);

    const popoutPane = React.useCallback(() => {
        const itemId = messageItem.ItemId?.Id;
        if (itemId) {
            popoutReadingPane(itemId);
        }
    }, [messageItem.ItemId]);

    if (item.id.indexOf('mailItem_header') >= 0) {
        return <MailSearchHeader item={item} />;
    }

    const attachmentPreviews = messageItem.ConversationId
        ? getAttachmentPreviews(messageItem.ConversationId)
        : [];

    const details = [messageItem.Subject, messageItem.Preview].join('\n');

    return (
        <div
            ref={ref}
            className={styles.searchResult}
            tabIndex={0}
            data-is-focusable={true}
            onFocusCapture={onFocusCapture}
            onBlurCapture={onBlurCapture}>
            <div className={styles.personaIconWrapper}>
                <PersonaIcon
                    name={messageItem.Sender?.Mailbox?.Name as string}
                    emailAddress={messageItem.Sender?.Mailbox?.EmailAddress as string}
                    showName={false}
                    personaSize={PersonaSize.size40}
                    tabIndex={-1}
                    personaCardBehaviorDisabled={true}
                />
            </div>
            <div className={styles.searchResultColumn}>
                <div className={styles.searchResultRow}>
                    <div className={styles.searchResultContent} title={details}>
                        <div className={styles.searchResultTopRow}>
                            <div className={styles.searchResultSender}>
                                {messageItem.Sender?.Mailbox?.Name}
                            </div>
                        </div>
                        <div
                            className={classNames(styles.searchResultTitle, {
                                [styles.unread]: !messageItem.IsRead,
                            })}>
                            {messageItem.Subject}
                        </div>
                        <div className={styles.searchResultPreview}>
                            <Icon className={styles.outlookLogo} iconName={ControlIcons.Mail} />
                            <span>{messageItem.Preview}</span>
                        </div>
                    </div>
                </div>
                {messageItem.HasAttachments &&
                    attachmentPreviews &&
                    attachmentPreviews.length > 0 && (
                        <AttachmentDocContainer
                            itemId={messageItem.ItemId?.Id || ''}
                            attachments={attachmentPreviews}
                            maxResults={1}
                        />
                    )}
            </div>
            <div className={styles.searchResultColumn}>
                <div className={styles.searchResultDate}>
                    {formatShortRelativeDate(messageItem.DateTimeReceived)}
                </div>
                <div
                    className={classNames(styles.searchResultOptions, {
                        [styles.optionsOpen]: optionsOpen,
                    })}>
                    <SearchResultOptions
                        isFocusable={hasFocusState}
                        assetRef={ref}
                        searchResultItem={item}
                        assetType={'mailItem'}
                        onOpen={popoutPane}
                        onOptionsOpened={setOptionsOpen}
                        onOptionsClosed={resetOptionsOpen}
                    />
                </div>
            </div>
        </div>
    );
});
